<template>
    <activix-popover
        placement="bottom-end"
        trigger="click"
        class="flex w-full h-full"
        ref="popover"
    >
        <template #trigger>
            <activix-tooltip :content="tooltip" :show-on-mobile="disabled && Boolean(tooltip)">
                <span class="flex w-full h-full">
                    <button
                        :data-testid="buttonTestid || buttonTestId || undefined"
                        type="button"
                        class="relative flex flex-col items-stretch grow items-center justify-center"
                        :class="[
                            {
                                'text-gray-500 cursor-not-allowed hover:bg-transparent': disabled,
                                'text-gray-700 hover:bg-gray-300': !disabled,
                            },
                            buttonClassName,
                        ]"
                        :aria-label="accessibleLabel || tooltip || undefined"
                        :aria-describedby="description ? descriptionId : undefined"
                        :aria-disabled="disabled"
                        @click.capture="handleClick"
                    >
                        <span class="flex items-center justify-center gap-2 pointer-events-none">
                            <span
                                data-testid="indicator"
                                class="absolute"
                                :class="[
                                    indicatorColor,
                                    indicatorClassName,
                                    verticalLayout
                                        ? 'left-0 h-6 w-1.5 rounded-r'
                                        : 'top-0 w-6 h-1.5 rounded-b',
                                ]"
                                v-if="indicator"
                            />

                            <slot name="before-icon" />

                            <icon
                                :class="[
                                    verticalLayout ? 'text-lg' : iconSizeClassName,
                                    iconClassName,
                                ]"
                                :name="iconName"
                            />

                            <slot name="after-icon" />
                        </span>

                        <slot name="under-icon" />
                        <span :id="descriptionId" class="sr-only" v-if="description">
                            {{ description }}
                        </span>
                    </button>
                </span>
            </activix-tooltip>
        </template>
        <slot name="popoverContent" :close-popover="closePopover" />
    </activix-popover>
</template>

<script>
    import { useId } from 'vue';
    import { ActivixPopover } from '@/components/activix-ui/index.ts';

    export const indicatorTypes = ['danger', 'info', 'success', 'warning'];

    export default {
        emits: ['click'],

        components: {
            ActivixPopover,
        },
        setup() {
            return { descriptionId: useId() };
        },
        props: {
            accessibleLabel: {
                type: String,
                default: '',
            },
            buttonClassName: {
                type: [String, Array, Object],
                default: '',
            },
            buttonTestId: {
                type: String,
                default: null,
            },
            // Applied to the inner clickable <button>, so callers can target the
            // element that fires the click rather than the popover wrapper (which
            // receives fallthrough attributes like `data-testid`).
            buttonTestid: {
                type: String,
                default: '',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            description: {
                type: String,
                default: '',
            },
            iconClassName: {
                type: [String, Array, Object],
                default: '',
            },
            iconName: {
                type: String,
                required: true,
            },
            iconSizeClassName: {
                type: [String, Array, Object],
                default: 'text-xl',
            },
            indicator: {
                type: Boolean,
                default: false,
            },
            indicatorClassName: {
                type: [String, Array, Object],
                default: '',
            },
            indicatorType: {
                type: String,
                default: 'info',
                validator: (value) => indicatorTypes.includes(value),
            },
            tooltip: {
                type: String,
                default: '',
            },
            verticalLayout: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            indicatorColor() {
                return {
                    danger: 'bg-red-500',
                    info: 'bg-blue-500',
                    success: 'bg-green-500',
                    warning: 'bg-orange-500',
                }[this.indicatorType];
            },
        },

        methods: {
            handleClick(event) {
                if (this.disabled) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    return;
                }

                this.$emit('click');
            },
            closePopover() {
                this.$refs.popover.close();
            },
        },
    };
</script>
